import { useEffect, useState } from "react";
import { api } from "../services/api";
import ConfirmModal from "../components/ConfirmModal";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import ParentSidebar from "../components/ParentSidebar";
import BottomNav from "../components/BottomNav";
import ThemeWrapper from "../components/ThemeWrapper";
import { useTheme } from "../hooks/useTheme";
import type { TaskRecurrence } from "@starquest/core";
import { TASK_LIBRARY } from "./taskLibrary";
import type { LibraryTask } from "./taskLibrary";
import styles from "./ParentHome.module.css";
import own from "./Tasks.module.css";

type Tab = "assign" | "pending" | "active";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function tierLabel(tier: string | null): { text: string; cls: string } | null {
  if (!tier)             return null;
  if (tier === "early")  return { text: "🌟 Early bird",  cls: own.tierEarly  };
  if (tier === "late")   return { text: "⏱️ Late",         cls: own.tierLate   };
  return                        { text: "✅ On time",      cls: own.tierOntime };
}

function recLabel(r: TaskRecurrence | null): string {
  if (!r) return "One-time";
  if (r.type === "daily") return "Daily";
  const dayNames = (r.days ?? []).map((d) => DAYS[d]).join(", ");
  return `Weekly (${dayNames})`;
}

export default function Tasks() {
  const { familyId } = useAuthStore();
  const { kids, taskAssignments, customTasks, setTaskAssignments, setCustomTasks, setKids } = useFamilyStore();
  const theme = useTheme();

  const [tab,     setTab]     = useState<Tab>("assign");
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);

  // Assignment modal state
  const [modal,      setModal]      = useState<LibraryTask & { taskId: string } | null>(null);
  const [kidIds,     setKidIds]     = useState<string[]>([]);
  const [rewardStr,  setRewardStr]  = useState("10");
  const [dueDatePart, setDueDatePart] = useState("");
  const [dueTimePart, setDueTimePart] = useState("");
  const [recType,    setRecType]    = useState<"none" | "daily" | "weekly">("none");
  const [weekDays,   setWeekDays]   = useState<number[]>([]);
  const [assigning,  setAssigning]  = useState(false);
  const [confirmRemoveAid, setConfirmRemoveAid] = useState<string | null>(null);

  // Custom task creation
  const [showCustom,  setShowCustom]  = useState(false);
  const [cName,       setCName]       = useState("");
  const [cEmoji,      setCEmoji]      = useState("");
  const [cReward,     setCReward]     = useState(10);
  const [savingCustom,setSavingCustom]= useState(false);

  // Inline weekly star goal edits
  const [goalEdits, setGoalEdits] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!familyId) return;
    setLoading(true);
    Promise.all([
      api.listKids(familyId).then((k) => setKids(k as import("@starquest/core").Kid[])),
      api.listTaskAssignments(familyId).then((a) => setTaskAssignments(a)),
      api.listTasks(familyId).then((t) => setCustomTasks(t)),
    ])
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [familyId]);

  function openModal(task: LibraryTask & { taskId: string }) {
    setModal(task);
    setKidIds(kids.length === 1 ? [kids[0].kidId] : []);
    setRewardStr(String(task.baseReward));
    setDueDatePart("");
    setDueTimePart("");
    setRecType("none");
    setWeekDays([]);
  }

  function toggleKid(kidId: string) {
    setKidIds((prev) => prev.includes(kidId) ? prev.filter((k) => k !== kidId) : [...prev, kidId]);
  }

  function toggleDay(day: number) {
    setWeekDays((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]);
  }

  async function assign() {
    if (!familyId || !modal) return;
    if (kidIds.length === 0)  { alert("Select at least one explorer"); return; }
    if (!dueTimePart)         { alert("Set a due time"); return; }
    if (recType === "none" && !dueDatePart) { alert("Set a due date"); return; }
    if (recType === "weekly" && weekDays.length === 0) { alert("Select at least one day"); return; }

    const recurrence: TaskRecurrence | null =
      recType === "none"   ? null :
      recType === "daily"  ? { type: "daily", days: [] } :
                             { type: "weekly", days: weekDays };

    function padDate(d: Date): string {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }

    let dueDateISO: string;
    if (recType === "none") {
      let dt = new Date(`${dueDatePart}T${dueTimePart}`);
      // If the chosen time has already passed, push to next day automatically
      if (dt.getTime() <= Date.now()) {
        dt = new Date(dt.getTime() + 24 * 60 * 60 * 1000);
      }
      dueDateISO = dt.toISOString();
    } else {
      // Recurring: first occurrence = today at chosen time; if past, start tomorrow
      let dt = new Date(`${padDate(new Date())}T${dueTimePart}`);
      if (dt.getTime() <= Date.now()) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        dt = new Date(`${padDate(tomorrow)}T${dueTimePart}`);
      }
      dueDateISO = dt.toISOString();
    }

    setAssigning(true);
    try {
      await Promise.all(kidIds.map((kidId) =>
        api.createTaskAssignment(familyId, {
          kidId,
          taskId:    modal.taskId,
          taskName:  modal.name,
          taskEmoji: modal.emoji,
          baseReward: Math.max(1, Number(rewardStr) || 1),
          xp:        modal.xp,
          dueDate:   dueDateISO,
          recurrence,
        })
      ));
      const updated = await api.listTaskAssignments(familyId);
      setTaskAssignments(updated);
      setModal(null);
    } catch { alert("Failed to assign. Try again."); }
    finally  { setAssigning(false); }
  }

  async function approve(aid: string) {
    if (!familyId) return;
    try {
      await api.reviewTaskAssignment(familyId, aid, "approve");
      const updated = await api.listTaskAssignments(familyId);
      setTaskAssignments(updated);
    } catch { alert("Failed to approve"); }
  }

  async function reject(aid: string) {
    if (!familyId) return;
    const reason = prompt("Rejection reason (optional):") ?? "";
    try {
      await api.reviewTaskAssignment(familyId, aid, "reject", reason);
      const updated = await api.listTaskAssignments(familyId);
      setTaskAssignments(updated);
    } catch { alert("Failed to reject"); }
  }

  async function removeAssignment(aid: string) {
    if (!familyId) return;
    setConfirmRemoveAid(aid);
  }

  async function confirmRemoveAssignment() {
    if (!familyId || !confirmRemoveAid) return;
    const aid = confirmRemoveAid;
    setConfirmRemoveAid(null);
    try {
      await api.deleteTaskAssignment(familyId, aid);
      const updated = await api.listTaskAssignments(familyId);
      setTaskAssignments(updated);
    } catch { alert("Failed to remove"); }
  }

  async function saveGoal(kidId: string) {
    if (!familyId) return;
    if (goalEdits[kidId] === undefined || goalEdits[kidId] === "") return;
    const val = Number(goalEdits[kidId]);
    if (isNaN(val) || val < 0) return;
    try {
      await api.updateKid(familyId, kidId, { weeklyPointGoal: val });
    } catch { alert("Failed to save goal"); }
  }

  async function saveCustomTask() {
    if (!familyId) return;
    if (!cName.trim()) { alert("Name is required"); return; }
    if (!cEmoji)       { alert("Emoji is required"); return; }
    setSavingCustom(true);
    try {
      await api.saveTask(familyId, { name: cName.trim(), emoji: cEmoji, baseReward: cReward, xp: cReward * 2 });
      const updated = await api.listTasks(familyId);
      setCustomTasks(updated);
      setShowCustom(false); setCName(""); setCEmoji(""); setCReward(10);
    } catch { alert("Failed to save custom task"); }
    finally  { setSavingCustom(false); }
  }

  const pendingAssignments = taskAssignments.filter((a) => a.status === "submitted");
  const activeAssignments  = taskAssignments.filter((a) => a.status === "assigned");

  function kidName(kidId: string) {
    return kids.find((k) => k.kidId === kidId)?.name ?? kidId.slice(0, 6);
  }
  function kidAvatar(kidId: string) {
    return kids.find((k) => k.kidId === kidId)?.avatar ?? "🧒";
  }

  const activeByKid = kids.map((k) => ({
    kid: k,
    assignments: activeAssignments.filter((a) => a.kidId === k.kidId),
  })).filter((g) => g.assignments.length > 0);

  if (loading) return (
    <div className={styles.page}>
      <ParentSidebar />
      <main className={styles.main} style={{ color: "rgba(240,240,255,0.5)", paddingTop: 60, textAlign: "center" }}>
        Loading tasks…
      </main>
      <BottomNav />
    </div>
  );

  return (
    <ThemeWrapper kid={null}>
    <div className={styles.page}>
      <ParentSidebar pendingCount={pendingAssignments.length} />
      <main className={styles.main}>
        <div className={styles.head}>
          <div>
            <h1 className={styles.h1}>📋 Tasks</h1>
            <p className={styles.sub}>Assign chores and routines to your {(theme.kidRole + "s").toLowerCase()}</p>
          </div>
        </div>

        {error && (
          <div style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 12, padding: "12px 16px", marginBottom: 16, color: "#f87171", fontSize: 13 }}>
            Failed to load tasks. Refresh to try again.
          </div>
        )}

        {/* Tabs */}
        <div className={own.tabs}>
          <button className={`${own.tab} ${tab === "assign"  ? own.tabActive : ""}`} onClick={() => setTab("assign")}>Assign</button>
          <button className={`${own.tab} ${tab === "pending" ? own.tabActive : ""}`} onClick={() => setTab("pending")} style={{ position: "relative" }}>
            Pending
            {pendingAssignments.length > 0 && <span className={own.tabBadge}>{pendingAssignments.length}</span>}
          </button>
          <button className={`${own.tab} ${tab === "active"  ? own.tabActive : ""}`} onClick={() => setTab("active")}>Active</button>
        </div>

        {/* ── ASSIGN TAB ── */}
        {tab === "assign" && (
          <div>
            {TASK_LIBRARY.map((cat) => (
              <div key={cat.category}>
                <div className={own.catLabel}>{cat.categoryEmoji} {cat.category}</div>
                <div className={own.libGrid}>
                  {cat.tasks.map((t) => (
                    <div key={t.name} className={own.libCard} onClick={() => openModal({ ...t, taskId: `library-${cat.category.toLowerCase().replace(/\s+/g, "-")}-${t.name.toLowerCase().replace(/\s+/g, "-")}` })}>
                      <div className={own.libEmoji}>{t.emoji}</div>
                      <div>
                        <div className={own.libName}>{t.name}</div>
                        <div className={own.libReward}>{t.baseReward} {theme.pointEmoji}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Custom tasks section */}
            <div className={own.catLabel}>✨ MY TASKS</div>
            <div className={own.libGrid}>
              {customTasks.map((t) => (
                <div key={t.taskId} className={own.libCard} onClick={() => openModal({ name: t.name, emoji: t.emoji, baseReward: t.baseReward, xp: t.xp, taskId: t.taskId })}>
                  <div className={own.libEmoji}>{t.emoji}</div>
                  <div>
                    <div className={own.libName}>{t.name}</div>
                    <div className={own.libReward}>{t.baseReward} {theme.pointEmoji}</div>
                  </div>
                </div>
              ))}
              <div className={`${own.libCard} ${own.customCard}`} onClick={() => setShowCustom(true)}>
                <div className={own.libEmoji}>➕</div>
                <div>
                  <div className={own.libName}>Create Custom Task</div>
                  <div className={own.libReward}>Set your own reward</div>
                </div>
              </div>
            </div>

            {/* Custom task form */}
            {showCustom && (
              <div className={own.customForm}>
                <div className={own.customFormField}>
                  <div className={own.customFormLabel}>Emoji</div>
                  <input className={own.input} style={{ width: 60, textAlign: "center" }} value={cEmoji} onChange={(e) => setCEmoji(e.target.value)} placeholder="🌟" maxLength={2} />
                </div>
                <div className={own.customFormField} style={{ flex: 1 }}>
                  <div className={own.customFormLabel}>Task Name</div>
                  <input className={own.input} value={cName} onChange={(e) => setCName(e.target.value)} placeholder="e.g. Feed the cat" />
                </div>
                <div className={own.customFormField}>
                  <div className={own.customFormLabel}>Base {theme.pointEmoji}</div>
                  <input className={own.input} style={{ width: 70 }} type="number" min={1} max={100} value={cReward} onChange={(e) => setCReward(Number(e.target.value))} />
                </div>
                <button type="button" className={own.saveBtn} style={{ width: "auto", padding: "10px 18px" }} onClick={saveCustomTask} disabled={savingCustom}>
                  {savingCustom ? "Saving…" : "Save"}
                </button>
                <button type="button" className={own.cancelBtn} style={{ padding: "10px 14px" }} onClick={() => setShowCustom(false)}>✕</button>
              </div>
            )}
          </div>
        )}

        {/* ── PENDING TAB ── */}
        {tab === "pending" && (
          <div>
            {pendingAssignments.length === 0 ? (
              <div className={own.empty}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                <div>No tasks waiting for review</div>
              </div>
            ) : (
              pendingAssignments.map((a) => {
                const tierInfo = tierLabel(a.tier);
                return (
                  <div key={a.assignmentId} className={own.pendingRow}>
                    <div className={own.pendingEmoji}>{a.taskEmoji}</div>
                    <div className={own.pendingInfo}>
                      <div className={own.pendingName}>{a.taskName}</div>
                      <div className={own.pendingMeta}>
                        {kidAvatar(a.kidId)} {kidName(a.kidId)} · Submitted {new Date(a.submittedAt!).toLocaleDateString()}
                      </div>
                    </div>
                    {tierInfo && <span className={`${own.tierBadge} ${tierInfo.cls}`}>{tierInfo.text}</span>}
                    <div className={own.pendingBtns}>
                      <button type="button" className={own.approveBtn} onClick={() => approve(a.assignmentId)}>✓ Approve</button>
                      <button type="button" className={own.rejectBtn}  onClick={() => reject(a.assignmentId)}>✕ Reject</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── ACTIVE TAB ── */}
        {tab === "active" && (
          <div>
            {activeByKid.length === 0 ? (
              <div className={own.empty}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                <div>No active task assignments — go to Assign to add some</div>
              </div>
            ) : (
              activeByKid.map(({ kid, assignments }) => (
                <div key={kid.kidId} className={own.kidGroup}>
                  <div className={own.kidGroupHead}>
                    <span style={{ fontSize: 22 }}>{kid.avatar}</span>
                    <span className={own.kidGroupName}>{kid.name}</span>
                    <span className={own.goalLabel}>Weekly goal:</span>
                    <input
                      type="number" min={0} max={9999}
                      className={own.goalInput}
                      value={goalEdits[kid.kidId] ?? (kid.weeklyPointGoal ?? "")}
                      onChange={(e) => setGoalEdits((prev) => ({ ...prev, [kid.kidId]: e.target.value }))}
                      onBlur={() => saveGoal(kid.kidId)}
                      placeholder="—"
                    />
                    <span className={own.goalLabel}>⭐ / week</span>
                  </div>
                  {assignments.map((a) => (
                    <div key={a.assignmentId} className={own.assignRow}>
                      <div className={own.assignEmoji}>{a.taskEmoji}</div>
                      <div className={own.assignInfo}>
                        <div className={own.assignName}>{a.taskName}</div>
                        <div className={own.assignMeta}>
                          Due {new Date(a.dueDate).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      <span className={own.recBadge}>{recLabel(a.recurrence)}</span>
                      <button type="button" className={own.removeBtn} onClick={() => removeAssignment(a.assignmentId)}>Remove</button>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        )}

        {/* ── ASSIGNMENT MODAL ── */}
        {modal && (
          <div className={own.modalOverlay} onClick={(e) => e.target === e.currentTarget && setModal(null)}>
            <div className={own.modal}>
              <div className={own.modalTask}>
                <div className={own.modalTaskEmoji}>{modal.emoji}</div>
                <div>
                  <div className={own.modalTaskName}>{modal.name}</div>
                  <div style={{ fontSize: 11, color: "rgba(240,240,255,0.5)", marginTop: 2 }}>Pick your settings below</div>
                </div>
              </div>

              {/* Kid selector */}
              <div className={own.formRow}>
                <label className={own.label}>Assign to</label>
                <div className={own.kidCheckGrid}>
                  {kids.map((k) => (
                    <div key={k.kidId} className={`${own.kidCheck} ${kidIds.includes(k.kidId) ? own.checked : ""}`} onClick={() => toggleKid(k.kidId)}>
                      <span style={{ fontSize: 18 }}>{k.avatar}</span>
                      <span className={own.kidCheckName}>{k.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Star reward */}
              <div className={own.formRow}>
                <label className={own.label}>Base Star Reward ⭐</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={own.input}
                  value={rewardStr}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, "");
                    setRewardStr(v);
                  }}
                  onBlur={() => {
                    const n = Number(rewardStr);
                    if (!rewardStr || n < 1) setRewardStr("1");
                  }}
                  placeholder="10"
                />
              </div>

              {/* Recurrence */}
              <div className={own.formRow}>
                <label className={own.label}>Repeat</label>
                <div className={own.recRow}>
                  {(["none", "daily", "weekly"] as const).map((r) => (
                    <button key={r} type="button" className={`${own.recBtn} ${recType === r ? own.recBtnActive : ""}`} onClick={() => setRecType(r)}>
                      {r === "none" ? "One-time" : r === "daily" ? "Daily" : "Weekly"}
                    </button>
                  ))}
                </div>
                {recType === "weekly" && (
                  <div className={own.dayGrid}>
                    {DAYS.map((d, i) => (
                      <button key={d} type="button" className={`${own.dayBtn} ${weekDays.includes(i) ? own.dayBtnActive : ""}`} onClick={() => toggleDay(i)}>
                        {d}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Date — only shown for one-time tasks */}
              {recType === "none" && (
                <div className={own.formRow}>
                  <label className={own.label}>Due Date</label>
                  <input
                    type="date"
                    className={own.input}
                    style={{ colorScheme: "dark" }}
                    value={dueDatePart}
                    onChange={(e) => setDueDatePart(e.target.value)}
                    onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
                  />
                </div>
              )}

              {/* Time — shown for all recurrence types */}
              <div className={own.formRow}>
                <label className={own.label}>{recType === "none" ? "Due Time" : "Time of Day"}</label>
                <input
                  type="time"
                  className={own.input}
                  style={{ colorScheme: "dark" }}
                  value={dueTimePart}
                  onChange={(e) => setDueTimePart(e.target.value)}
                  onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
                />
                {(() => {
                  if (!dueTimePart) return null;
                  const dateBase = recType === "none" ? dueDatePart : (() => {
                    const d = new Date();
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  })();
                  if (!dateBase) return null;
                  const dt = new Date(`${dateBase}T${dueTimePart}`);
                  if (dt.getTime() > Date.now()) return null;
                  return (
                    <div style={{ marginTop: 6, padding: "6px 10px", background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 8, fontSize: 11, color: "rgba(251,191,36,0.9)", lineHeight: 1.4 }}>
                      ⚠️ This time has already passed — task will be scheduled for tomorrow at {dueTimePart}
                    </div>
                  );
                })()}
              </div>

              <div className={own.modalBtns}>
                <button type="button" className={own.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
                <button type="button" className={own.saveBtn} onClick={assign} disabled={assigning}>
                  {assigning ? "Assigning…" : `Assign${kidIds.length > 1 ? ` to ${kidIds.length} explorers` : ""}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <BottomNav />
      {confirmRemoveAid && (() => {
        const task = taskAssignments.find((a) => a.assignmentId === confirmRemoveAid);
        const name = task?.taskName || "this task";
        return (
          <ConfirmModal
            message={`Remove "${name}"?`}
            detail="The explorer will no longer see this task assignment."
            confirmLabel="Remove"
            typeToConfirm={name}
            onCancel={() => setConfirmRemoveAid(null)}
            onConfirm={confirmRemoveAssignment}
          />
        );
      })()}
    </div>
    </ThemeWrapper>
  );
}
